// ─────────────────────────────────────────────────────────────────────────────
//  LocationChip.js — Swiggy/Zomato-style header pill that shows the current
//  city and opens the CityPickerModal on tap. Lazy-loads the modal so the
//  initial home-page paint stays light.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, Suspense } from 'react';
import { MdLocationOn, MdKeyboardArrowDown } from 'react-icons/md';
import { useLocation } from '../context/LocationContext';

const CityPickerModal = React.lazy(() => import('./CityPickerModal'));

export default function LocationChip({ compact = false, onClick }) {
  const { city, area, source, requesting, permission, accuracy } = useLocation();
  const [open, setOpen] = useState(false);

  const label = city || (requesting ? 'Detecting…' : 'Set location');
  const sub   = requesting
    ? 'Getting GPS…'
    : source === 'gps'
      ? (accuracy && accuracy < 2000 ? 'Current location · GPS' : 'Current location')
      : source === 'ip'
        ? 'Approximate · tap to use GPS'
        : source === 'manual'
          ? 'City selected · tap to change'
          : area && area.toLowerCase() !== (city || '').toLowerCase()
            ? area
            : permission === 'denied'
              ? 'Pick city manually'
              : 'Set your location';

  const handle = () => {
    if (onClick) onClick();
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handle}
        className={`location-chip ${compact ? 'is-compact' : ''} ${source === 'gps' ? 'is-gps' : ''}`}
        aria-label="Change location"
      >
        <MdLocationOn className="loc-chip-icon" />
        <span className="loc-chip-text">
          <span className="loc-chip-city">{label}</span>
          {sub && !compact && <span className="loc-chip-sub">{sub}</span>}
        </span>
        <MdKeyboardArrowDown className="loc-chip-caret" />
      </button>

      {open && (
        <Suspense fallback={null}>
          <CityPickerModal onClose={() => setOpen(false)} />
        </Suspense>
      )}
    </>
  );
}
